import { useEffect, useState } from "react";
import { api, apiError, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, Wrench } from "lucide-react";

const EQ_STATUS = { AVAILABLE: "#34D399", "IN USE": "#60A5FA", MAINTENANCE: "#F59E0B", "OUT OF SERVICE": "#F87171" };
const ISSUE_STATUS = ["OPEN", "IN REVIEW", "REPAIRING", "RESOLVED"];

export default function Workshop() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [equipment, setEquipment] = useState([]);
  const [issues, setIssues] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ equipment_id: "", equipment_name: "", problem: "", urgency: "STANDARD", description: "" });

  const load = () => { api.get("/equipment").then((r) => setEquipment(r.data)).catch(() => {}); api.get("/equipment-issues").then((r) => setIssues(r.data)).catch(() => {}); };
  useEffect(() => { load(); }, []);

  const report = async () => {
    if (!f.equipment_id || !f.problem) return toast.error("Select equipment and describe the problem");
    const eq = equipment.find((e) => e.id === f.equipment_id);
    try { await api.post("/equipment-issues", { ...f, equipment_name: eq?.name || "" }); toast.success("Issue reported to 3B"); setOpen(false); setF({ equipment_id: "", equipment_name: "", problem: "", urgency: "STANDARD", description: "" }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const setEqStatus = async (id, status) => { try { await api.patch(`/equipment/${id}`, { status }); load(); } catch { toast.error("Failed"); } };
  const setIssueStatus = async (id, status) => { try { await api.patch(`/equipment-issues/${id}`, { status }); toast.success("Updated"); load(); } catch { toast.error("Failed"); } };
  const inputCls = "w-full panel rounded-sm px-3 py-2.5 text-sm outline-none focus:border-[#C5A059]";

  return (
    <div>
      <PageHeader eyebrow="Hatton Garden" title="Workshop">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><button data-testid="report-issue-btn" className="btn-brass flex items-center gap-1.5 px-4 py-2.5 rounded-sm text-sm"><AlertTriangle size={15} /> Report Issue</button></DialogTrigger>
          <DialogContent className="bg-[#131316] border-[#28282D] text-[#F5F3EF]">
            <DialogHeader><DialogTitle className="font-serif text-2xl">Report Equipment Issue</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-2">
              <div><label className="eyebrow block mb-1.5">Equipment</label>
                <select value={f.equipment_id} onChange={(e) => setF((p) => ({ ...p, equipment_id: e.target.value }))} className={inputCls} data-testid="issue-equipment">
                  <option value="">Select…</option>{equipment.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>
              <div><label className="eyebrow block mb-1.5">Problem</label><input data-testid="issue-problem" value={f.problem} onChange={(e) => setF((p) => ({ ...p, problem: e.target.value }))} className={inputCls} placeholder="e.g. Motor overheating" /></div>
              <div><label className="eyebrow block mb-1.5">Urgency</label>
                <div className="flex gap-2">{["STANDARD", "URGENT"].map((u) => (
                  <button key={u} onClick={() => setF((p) => ({ ...p, urgency: u }))} className={`flex-1 py-2.5 rounded-sm text-xs uppercase border transition-all ${f.urgency === u ? (u === "URGENT" ? "border-[rgba(245,158,11,0.5)] bg-[rgba(245,158,11,0.12)] text-[#F59E0B]" : "border-[#C5A059] bg-[#1A1A1E] text-[#F5F3EF]") : "hairline text-[#8C867A]"}`}>{u}</button>
                ))}</div>
              </div>
              <div><label className="eyebrow block mb-1.5">Description</label><textarea value={f.description} onChange={(e) => setF((p) => ({ ...p, description: e.target.value }))} rows={3} className={inputCls} /></div>
              <button onClick={report} data-testid="submit-issue" className="btn-brass w-full py-2.5 rounded-sm text-sm">Submit Report</button>
            </div>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="eyebrow mb-3">Equipment Directory</div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
        {equipment.map((e) => (
          <div key={e.id} data-testid={`equipment-${e.id}`} className="panel rounded-sm p-5">
            <div className="flex items-start justify-between mb-2">
              <div className="w-10 h-10 rounded-sm flex items-center justify-center" style={{ background: "#1A1A1E" }}><Wrench size={17} className="brass-text" /></div>
              <span className="text-[0.6rem] uppercase tracking-wider px-2 py-1 rounded-sm" style={{ color: EQ_STATUS[e.status], background: `${EQ_STATUS[e.status]}18` }}>{e.status}</span>
            </div>
            <div className="font-serif text-lg text-[#F5F3EF]">{e.name}</div>
            <div className="text-xs text-[#8C867A]">{e.category}</div>
            {isAdmin && (
              <select value={e.status} onChange={(ev) => setEqStatus(e.id, ev.target.value)} data-testid={`eq-status-${e.id}`}
                className="mt-3 w-full panel rounded-sm px-2 py-1.5 text-xs outline-none">
                {Object.keys(EQ_STATUS).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            )}
          </div>
        ))}
      </div>

      <div className="eyebrow mb-3">Equipment Issues</div>
      <div className="panel rounded-sm overflow-hidden">
        <div className="divide-y" style={{ borderColor: "#28282D" }}>
          {issues.length === 0 && <div className="px-4 py-8 text-center text-sm text-[#8C867A]">No issues reported.</div>}
          {issues.map((i) => (
            <div key={i.id} data-testid={`issue-${i.id}`} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3.5">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[#E2DFD7]">{i.equipment_name}</span>
                  {i.urgency === "URGENT" && <span className="text-[0.6rem] uppercase tracking-wider px-1.5 py-0.5 rounded-sm" style={{ color: "#F59E0B", background: "rgba(245,158,11,0.12)" }}>Urgent</span>}
                </div>
                <div className="text-xs text-[#8C867A] mt-0.5">{i.problem} · {i.reported_by} · {fmtDateTime(i.created_at)}</div>
              </div>
              {isAdmin ? (
                <select value={i.status} onChange={(e) => setIssueStatus(i.id, e.target.value)} data-testid={`issue-status-${i.id}`}
                  className="panel rounded-sm px-2 py-1.5 text-xs outline-none">{ISSUE_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}</select>
              ) : <span className="text-[0.65rem] uppercase tracking-wider px-2 py-1 rounded-sm" style={{ color: i.status === "RESOLVED" ? "#34D399" : "#D4AF37", background: "#1A1A1E" }}>{i.status}</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
